import React from 'react';
import { Download, FileText, FileCode2, CheckCircle2, Loader2 } from 'lucide-react';

interface ExportStep {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  description?: string;
}

interface ExportLoadingModalProps {
  isOpen: boolean;
  steps: ExportStep[];
  currentStep?: string;
  progress?: number; // 0-100
  title?: string;
  subtitle?: string;
}

const ExportLoadingModal: React.FC<ExportLoadingModalProps> = ({
  isOpen,
  steps,
  currentStep,
  progress = 0,
  title = "Exporting Comprehensive Report",
  subtitle = "Generating comprehensive report..."
}) => {
  if (!isOpen) return null;

  const getStepIcon = (step: ExportStep) => {
    const IconComponent = step.icon;
    
    if (step.status === 'completed') {
      return <CheckCircle2 size={20} className="text-emerald-500" />;
    } else if (step.status === 'in-progress') {
      return <Loader2 size={20} className="text-blue-500 animate-spin" />;
    } else if (step.status === 'error') {
      return <IconComponent size={20} className="text-red-500" />;
    } else {
      return <IconComponent size={20} className="text-gray-400" />;
    }
  };

  const getStepTextColor = (step: ExportStep) => {
    switch (step.status) {
      case 'completed':
        return 'text-emerald-700';
      case 'in-progress':
        return 'text-blue-700';
      case 'error':
        return 'text-red-700';
      default:
        return 'text-gray-500';
    }
  };

  const getStepBgColor = (step: ExportStep) => {
    switch (step.status) {
      case 'completed':
        return 'bg-emerald-50 border-emerald-200';
      case 'in-progress':
        return 'bg-blue-50 border-blue-200 ring-2 ring-blue-200/50';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const completedSteps = steps.filter(step => step.status === 'completed').length;
  const totalSteps = steps.length;
  // Use provided progress if available, otherwise calculate from steps
  const displayProgress = progress > 0 ? progress : (totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 z-[10001]">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-lg border border-white/20">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 rounded-full p-2">
                  <Download className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">{title}</h2>
                  <p className="text-blue-100 text-sm">{subtitle}</p>
                </div>
              </div>
              <div className="text-white/90 text-sm font-medium">
                {completedSteps}/{totalSteps}
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 backdrop-blur-3xl"></div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.max(5, displayProgress)}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-gray-600">Progress</span>
            <span className="text-xs font-medium text-gray-700">{Math.round(displayProgress)}%</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 bg-gradient-to-b from-gray-50/50 to-white">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Export Steps
            </h3>
            <div className="space-y-3">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className={`p-3 rounded-xl border-2 transition-all duration-300 ${getStepBgColor(step)}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {getStepIcon(step)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm ${getStepTextColor(step)}`}>
                        {step.label}
                      </div>
                      {step.description && (
                        <div className="text-xs text-gray-500 mt-1">
                          {step.description}
                        </div>
                      )}
                    </div>
                    {step.status === 'in-progress' && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Current Status */}
          {currentStep && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50/30 p-4 rounded-xl border border-blue-200/50">
              <div className="flex items-center space-x-2">
                <Loader2 size={16} className="text-blue-600 animate-spin" />
                <span className="text-sm font-medium text-blue-900">
                  {steps.find(s => s.id === currentStep)?.label || 'Processing...'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <div className="text-center">
            <p className="text-xs text-gray-500">
              Please wait while we generate your comprehensive report...
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportLoadingModal;
